export const SFH_PRICE_CAP = 1_500_000;
export const ITBI_FIRST_BUY_FACTOR = 0.5;
export const DEFAULT_FINANCING_TERM_MONTHS = 360;
export const DEFAULT_FINANCING_ANNUAL_RATE = 0.1195;

export const ITBI_RATES = Object.freeze({
  default: 0.03,
  SP: {
    sao_paulo: 0.03,
    campinas: 0.027,
    santos: 0.03,
  },
  RJ: {
    rio_de_janeiro: 0.02,
  },
  MG: {
    belo_horizonte: 0.03,
  },
});

const NOTARY_BRACKETS = [
  { max: 250_000, rate: 0.022, fixed: 600 },
  { max: 500_000, rate: 0.02, fixed: 700 },
  { max: 1_000_000, rate: 0.018, fixed: 900 },
  { max: Infinity, rate: 0.016, fixed: 1200 },
];

export const numberValue = value => {
  if (value === '' || value === null || value === undefined) return 0;
  const normalized =
    typeof value === 'string'
      ? value
          .replace(/\./g, '')
          .replace(',', '.')
          .replace(/[^\d.]/g, '')
      : value;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
};

const asciiText = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const itbiRateFor = ({ uf, city } = {}) => {
  const ratesForUf = ITBI_RATES[String(uf || '').toUpperCase()];
  if (!ratesForUf) return ITBI_RATES.default;

  return (
    ratesForUf[asciiText(city)] || ratesForUf.default || ITBI_RATES.default
  );
};

export const isFinancingPaymentKind = value =>
  ['financiamento', 'financiamento_fgts'].includes(String(value || ''));

export const isSfhEligible = (propertyValue, cap = SFH_PRICE_CAP) => {
  const value = numberValue(propertyValue);
  return value > 0 && value <= cap;
};

export const computeItbi = ({
  propertyValue,
  uf,
  city,
  firstPurchase = false,
  hasSfh = false,
  overridden = false,
  overrideValue,
} = {}) => {
  if (overridden) return Math.ceil(numberValue(overrideValue));

  const value = numberValue(propertyValue);
  if (!value) return 0;

  const discountFactor = firstPurchase && hasSfh ? ITBI_FIRST_BUY_FACTOR : 1;
  return Math.ceil(value * itbiRateFor({ uf, city }) * discountFactor);
};

export const computeNotaryFees = ({
  propertyValue,
  overridden = false,
  overrideValue,
} = {}) => {
  if (overridden) return Math.ceil(numberValue(overrideValue));

  const value = numberValue(propertyValue);
  if (!value) return 0;

  const bracket = NOTARY_BRACKETS.find(item => value <= item.max);
  return Math.ceil(value * bracket.rate + bracket.fixed);
};

export const computeBrokerageFee = ({
  propertyValue,
  brokerageFeeValue,
  brokerageRate = 0.06,
} = {}) => {
  const explicitValue = numberValue(brokerageFeeValue);
  if (explicitValue > 0) return Math.ceil(explicitValue);

  const value = numberValue(propertyValue);
  return value ? Math.ceil(value * brokerageRate) : 0;
};

export const computeMonthlyInstallment = ({
  financedAmount,
  annualRate = DEFAULT_FINANCING_ANNUAL_RATE,
  termMonths = DEFAULT_FINANCING_TERM_MONTHS,
} = {}) => {
  const amount = numberValue(financedAmount);
  const months =
    Number(termMonths) > 0 ? Number(termMonths) : DEFAULT_FINANCING_TERM_MONTHS;
  const yearlyRate =
    Number(annualRate) > 1 ? Number(annualRate) / 100 : Number(annualRate);
  const monthlyRate = (yearlyRate || DEFAULT_FINANCING_ANNUAL_RATE) / 12;

  if (!amount) return 0;
  if (!monthlyRate) return Math.ceil(amount / months);

  return Math.ceil(
    (amount * (monthlyRate * (1 + monthlyRate) ** months)) /
      ((1 + monthlyRate) ** months - 1)
  );
};

const dialogs = {
  SALE_PRICE: {
    title: 'Valor final da venda',
    description:
      'Valor combinado para a compra do imóvel. As taxas de aquisição são calculadas a partir dele.',
  },
  ITBI: {
    title: 'ITBI',
    description:
      'Imposto municipal de transmissão do imóvel, estimado por cidade e UF quando a alíquota está disponível.',
    items: [
      'O valor é arredondado para cima.',
      'Primeira compra com SFH aplica o desconto configurado na simulação.',
      'Use o override quando o cartório ou município informar um valor diferente.',
    ],
  },
  NOTARY_FEES: {
    title: 'Cartório e registro',
    description:
      'Estimativa de escritura, registro e vistoria por faixa de valor do imóvel.',
    items: [
      'Os valores finais podem variar por cartório.',
      'Use o override quando houver orçamento confirmado.',
    ],
  },
  BROKERAGE_FEE: {
    title: 'Corretagem',
    description:
      'Taxa comercial da intermediação. Quando não informada manualmente, a simulação usa 6% do valor final.',
  },
};

export const buildPurchasePaymentInfo = ({
  propertyValue,
  paymentKind,
  uf,
  city,
  firstPurchase = false,
  hasSfh = false,
  itbiValue,
  itbiOverridden = false,
  notaryFeeValue,
  notaryFeeOverridden = false,
  brokerageFeeValue,
  financedAmount,
  termMonths = DEFAULT_FINANCING_TERM_MONTHS,
  annualRate = DEFAULT_FINANCING_ANNUAL_RATE,
} = {}) => {
  const value = numberValue(propertyValue);
  if (!value) {
    return {
      rows: [],
      fees: {},
      total: 0,
      financing: null,
      sfhEligible: false,
      hasSfh: false,
    };
  }

  const financing = isFinancingPaymentKind(paymentKind);
  const sfhEligible = financing && isSfhEligible(value);
  const normalizedHasSfh = Boolean(hasSfh && sfhEligible);
  const itbi = computeItbi({
    propertyValue: value,
    uf,
    city,
    firstPurchase,
    hasSfh: normalizedHasSfh,
    overridden: itbiOverridden,
    overrideValue: itbiValue,
  });
  const notary = computeNotaryFees({
    propertyValue: value,
    overridden: notaryFeeOverridden,
    overrideValue: notaryFeeValue,
  });
  const brokerage = computeBrokerageFee({
    propertyValue: value,
    brokerageFeeValue,
  });

  const rows = [
    {
      kind: 'SALE_PRICE',
      label: 'Valor final da venda',
      value,
      dialog: dialogs.SALE_PRICE,
    },
    {
      kind: 'ITBI',
      label:
        firstPurchase && normalizedHasSfh
          ? 'ITBI com desconto'
          : 'ITBI estimado',
      value: itbi,
      dialog: dialogs.ITBI,
    },
    {
      kind: 'NOTARY_FEES',
      label: 'Cartório e registro',
      value: notary,
      dialog: dialogs.NOTARY_FEES,
    },
    {
      kind: 'BROKERAGE_FEE',
      label: 'Corretagem',
      value: brokerage,
      dialog: dialogs.BROKERAGE_FEE,
    },
  ];
  const total = Math.ceil(
    rows.reduce((sum, row) => sum + numberValue(row.value), 0)
  );

  return {
    rows: [
      ...rows,
      {
        kind: 'TOTAL_COST',
        label: 'Custo total estimado',
        value: total,
      },
    ],
    fees: { itbi, notary, brokerage },
    total,
    financing: financing
      ? {
          financedAmount: numberValue(financedAmount),
          monthlyInstallment: computeMonthlyInstallment({
            financedAmount,
            annualRate,
            termMonths,
          }),
          termMonths,
          annualRate,
        }
      : null,
    sfhEligible,
    hasSfh: normalizedHasSfh,
  };
};
